import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { environment } from '../../../../environments/environment.development';
import { BehaviorSubject, catchError, map, Observable, tap } from 'rxjs';
import {
  ApiResponse,
  ApiResponseOrgScheduleListPaginated,
  ApiResponseOrgSchedulePaginated,
  AssignTemplateModel,
  OrgScheduleList,
  OrgScheduleQueryParams,
  OrgSchedules,
  PaginatedResult,
  ScheduleApiResponse,
  ScheduleListQueryParams,
  TemplateModel,
} from '../manage-settings.interface';
import { ApiRespons } from '../../manage-organization/manage-organization.interface';

@Injectable({
  providedIn: 'root',
})
export class CustomTimeService {
  private readonly BASE_URL = environment.apiUrl;
  private readonly ORGANIZATION_URL = `${this.BASE_URL}/OrganizationSchedule`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);

  private readonly organizationsSubject =
    new BehaviorSubject<ApiResponseOrgSchedulePaginated | null>(null);
  readonly organizations$ = this.organizationsSubject.asObservable();

  private readonly schedulesSubject =
    new BehaviorSubject<ApiResponseOrgScheduleListPaginated | null>(null);
  readonly schedules$ = this.schedulesSubject.asObservable();

  getOrganizationSchedule(
    queryParams: OrgScheduleQueryParams
  ): Observable<ApiResponseOrgSchedulePaginated> {
    return this.http
      .get<ApiResponseOrgSchedulePaginated>(
        `${this.ORGANIZATION_URL}/org-assigned-schedule-list`,
        {
          params: this.buildQueryParams(queryParams),
        }
      )
      .pipe(
        tap(response => this.organizationsSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    templateName,
    OrganizationName,
    organizationId,
  }: OrgScheduleQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sort) {
      params = params.set('sort', sort);
    }

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }
    if (templateName) {
      params = params.set('templateName', templateName);
    }
    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }
    if (OrganizationName) {
      params = params.set('OrganizationName', OrganizationName);
    }

    return params;
  }

  getOrgScheduleList(params: ScheduleListQueryParams) {
    return this.http
      .get<ApiResponseOrgScheduleListPaginated>(
        `${this.ORGANIZATION_URL}/org-schedule-list`,
        {
          params: this.buildScheduleQueryParams(params),
        }
      )
      .pipe(
        tap(response => this.schedulesSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildScheduleQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    templateName,
    IsDropDownRequest,
    organizationId,
  }: ScheduleListQueryParams): HttpParams {
    let params = new HttpParams();
    if (pageIndex) {
      params.set('Page', pageIndex.toString());
    }
    if (pageSize) {
      params.set('PageSize', pageSize.toString());
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }
    if (templateName) {
      params = params.set('templateName', templateName);
    }
    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }
    if (IsDropDownRequest) {
      params = params.set('IsDropDownRequest', IsDropDownRequest.toString());
    }

    return params;
  }

  getOrgSechduleById(id: number) {
    return this.http
      .get<ApiRespons<OrgScheduleList>>(`${this.ORGANIZATION_URL}/${id}`)
      .pipe(
        map(response => {
          if (response.result === null) {
            throw new Error('Template not found');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAssignedScheduleListByOrgId(id: number, IsIncludeDefaultSchedule = false) {
    let url = `${this.ORGANIZATION_URL}/org-assigned-schedule-list?OrganizationId=${id}`;
    if (IsIncludeDefaultSchedule) {
      url += '&IsIncludeDefaultSchedule=true';
    }
    return this.http.get<ApiResponse<PaginatedResult<OrgSchedules>>>(url).pipe(
      map(response => {
        return response.result.items[0].scheduleTemplates;
      }),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  createTemplate(paylaod: TemplateModel): Observable<ApiResponse<void>> {
    return this.http
      .post<ApiResponse<void>>(`${this.ORGANIZATION_URL}/create`, paylaod)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  updateTemplate(paylaod: TemplateModel): Observable<ApiResponse<void>> {
    return this.http
      .put<ApiResponse<void>>(`${this.ORGANIZATION_URL}/update`, paylaod)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createOrganizationSchedule(
    paylaod: AssignTemplateModel
  ): Observable<ApiResponse<void>> {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.ORGANIZATION_URL}/create-organization-schedule`, paylaod)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteOrganizationSchedule(paylaod: number[]): Observable<ApiResponse<void>> {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.ORGANIZATION_URL}/delete-assign-schedules`, { body: paylaod })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteBulkOrganizationSchedule(
    paylaod: number[]
  ): Observable<ScheduleApiResponse<null>> {
    return this.http
      .delete<
        ScheduleApiResponse<null>
      >(`${this.ORGANIZATION_URL}/bulk-delete-assign-schedules`, { body: paylaod })
      .pipe(
        map(response => {
          return response as ScheduleApiResponse<null>;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
}
